import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiBell, FiSearch, FiMenu, FiFolder, FiCheckSquare, FiX } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { searchAPI, notificationAPI } from '../services/api';
import './Header.css';

const PCOL = { low: '#22c55e', medium: '#f59e0b', high: '#ef4444', critical: '#7c3aed' };
const SCOL = {
  todo: '#94a3b8', 'in-progress': '#f59e0b', review: 'var(--pm-primary)',
  completed: '#22c55e', blocked: '#ef4444', active: '#22c55e',
  planning: 'var(--pm-secondary)', 'on-hold': '#f59e0b', cancelled: '#ef4444',
};

const debounce = (fn, ms) => {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
};

const Header = ({ toggleSidebar }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [unread, setUnread] = useState(0);
  const searchRef = useRef(null);

  // Fetch unread count
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const r = await notificationAPI.getAll({ unreadOnly: true, limit: 1 });
        setUnread(r.data.unreadCount || 0);
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const doSearch = useCallback(debounce(async (q) => {
    if (q.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    try {
      const r = await searchAPI.global(q);
      setResults(r.data.results || []);
    } catch { setResults([]); }
    setSearching(false);
  }, 300), []);

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    setShowDropdown(true);
    doSearch(v);
  };

  const handleSelect = (result) => {
    navigate(result.url);
    setQuery('');
    setResults([]);
    setShowDropdown(false);
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setShowDropdown(false);
  };

  return (
    <header className="header">
      <div className="header-left">
        <button className="menu-btn" onClick={toggleSidebar}>
          <FiMenu />
        </button>

        {/* Global Search */}
        <div className="search-wrapper" ref={searchRef}>
          <div className="search-bar" style={{ position: 'relative' }}>
            <FiSearch color="#94a3b8" />
            <input
              type="text"
              placeholder="Search projects, tasks... (⌘K)"
              value={query}
              onChange={handleInput}
              onFocus={() => query.length >= 2 && setShowDropdown(true)}
            />
            {query && (
              <button onClick={clearSearch} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#94a3b8', display: 'flex', alignItems: 'center' }}>
                <FiX size={14} />
              </button>
            )}
          </div>

          {/* Dropdown Results */}
          {showDropdown && query.length >= 2 && (
            <div className="search-dropdown">
              {searching && (
                <div style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 14, height: 14, border: '2px solid #e2e8f0', borderTop: '2px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite', flexShrink: 0 }} />
                  Searching...
                </div>
              )}
              {!searching && results.length === 0 && (
                <div style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 13, textAlign: 'center' }}>
                  No results for "{query}"
                </div>
              )}
              {!searching && results.length > 0 && (
                <>
                  {['project', 'task'].map(type => {
                    const group = results.filter(r => r.type === type);
                    if (!group.length) return null;
                    return (
                      <div key={type}>
                        <div style={{ padding: '8px 16px 4px', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                          {type === 'project' ? '📁 Projects' : '✅ Tasks'}
                        </div>
                        {group.map(r => (
                          <div
                            key={r._id}
                            onClick={() => handleSelect(r)}
                            className="search-result-item"
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={{ color: '#94a3b8', flexShrink: 0 }}>
                                {r.type === 'project' ? <FiFolder size={14} /> : <FiCheckSquare size={14} />}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</div>
                                {r.subtitle && <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.subtitle}</div>}
                              </div>
                              <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                                {r.status && (
                                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 6, background: (SCOL[r.status] || '#94a3b8') + '20', color: SCOL[r.status] || '#94a3b8', textTransform: 'capitalize' }}>
                                    {r.status}
                                  </span>
                                )}
                                {r.priority && (
                                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: PCOL[r.priority] || '#94a3b8', display: 'inline-block', alignSelf: 'center' }} title={r.priority} />
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="header-right">
        {/* Notification Bell */}
        <button
          className="notification-btn"
          onClick={() => navigate('/notifications')}
          title="Notifications"
        >
          <FiBell />
          {unread > 0 && (
            <span className="badge">{unread > 99 ? '99+' : unread}</span>
          )}
        </button>

        <div className="user-menu" onClick={() => navigate('/settings')}>
          <div className="user-avatar" style={{ overflow:'hidden', padding:0 }}>
            {user?.avatar
              ? <img src={user.avatar} alt={user.name} style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%' }}/>
              : user?.name?.charAt(0)?.toUpperCase()
            }
          </div>
          <span className="user-name">{user?.name}</span>
        </div>
      </div>
    </header>
  );
};

export default Header;
